/**
 * DWEB-229 — a nav scroll that RE-RESOLVES its target while it flies.
 *
 * THE DEFECT. `scroller.scrollTo(name, { offset })` (react-scroll 1.9.3,
 * `mixins/scroller.js`) computes `scrollOffset(container, target) + offset`
 * ONCE, at aim time, and then animates to that fixed number. That is correct
 * only if the document above the target does not move during the flight. On
 * this site it does: sections above `#about` collapse as the hero leaves the
 * viewport, so a perfectly-aimed scroll arrives somewhere else. Measured on the
 * built artifact at 390x844, the About heading is aimed at +106 px and settles
 * at **-175.9 px** — entirely above the viewport — in run after run, with the
 * same landing across flights whose durations differ by 483 ms. The aim is
 * wrong, not the flight.
 *
 * 🔴 THE FIX IS NOT AN OFFSET. Adding the collapse back as a number would be a
 * magic constant fitted to one viewport, at one moment, for one route: the
 * phone and desktop collapses differ, and both are a function of what has
 * lazily mounted. This animator instead recomputes the destination from the
 * element's CURRENT position on every frame, so whatever moves above it is
 * already accounted for by the next frame. Nothing is fitted and nothing is
 * vendored: react-scroll stays installed and `Element` still registers the
 * names — only `scroller.get(name)`, its public lookup, is used here.
 *
 * 🔴 IT COMPENSATES; IT DOES NOT CURE. The collapse is still there. DWEB-181
 * (the hero's margin toggling with header visibility, and the lazy sections
 * above) stays OPEN. This makes the scroll immune to it, which is a different
 * claim from fixing it.
 */

import { scroller } from 'react-scroll';
import { headerHeight } from './headerHeight';

/**
 * The exponential time constant of the approach, in ms.
 *
 * WHY 120. The retired call sites all passed react-scroll `duration: 500`. An
 * exponential approach covers 99% of its distance in ~4.6 time constants, so
 * 120 ms reproduces that 500 ms feel (4.6 x 120 = 552 ms) while making the
 * position a function of the CURRENT target rather than a fixed one. It is a
 * time constant and not a duration on purpose: a duration cannot be honoured
 * when the destination is still moving.
 */
export const SCROLL_TIME_CONSTANT_MS = 120;

/**
 * TERMINATION GUARANTEE, part 1 of 2 — the unconditional ceiling, in ms.
 *
 * WHY 3000. The observed flight is ~500 ms and the worst observed collapse has
 * finished settling inside ~1 s, so this is roughly six times the longest real
 * case. It exists because a moving target admits, in principle, a target that
 * never stops: a page reflowing forever would otherwise be chased forever. At
 * that point continuing is visible jitter rather than a landing, so the loop
 * stops where it is. Checked on EVERY frame, before anything else can continue
 * the loop, so it bounds the run whatever the convergence test does.
 */
export const SCROLL_MAX_DURATION_MS = 3000;

/**
 * TERMINATION GUARANTEE, part 2 of 2 — the convergence condition.
 *
 * The run finishes early when BOTH hold: the destination has moved less than
 * `SCROLL_CONVERGENCE_EPSILON_PX` for `SCROLL_CONVERGENCE_FRAMES` consecutive
 * frames (the document above has stopped collapsing), AND we are within that
 * same epsilon of it (we have arrived). Either alone is insufficient: a still
 * target we have not reached is mid-flight, and arriving at a target that is
 * still moving is exactly the defect above.
 *
 * WHY 0.5 px: layout reads are fractional and browsers round a scroll position,
 * so exact equality never holds. WHY 3 frames: ~50 ms at 60 Hz, long enough to
 * outlast a single reflow frame and short enough that it cannot become a fixed
 * wait in disguise.
 */
export const SCROLL_CONVERGENCE_EPSILON_PX = 0.5;
export const SCROLL_CONVERGENCE_FRAMES = 3;

/**
 * The smallest step worth taking, in px.
 *
 * 🔴 WHY THIS EXISTS, AND IT IS NOT A FUDGE — the first build of this animator
 * had no such clamp and MEASURED a 3.06 px residual on phone / 2.15 px on
 * desktop, above the 2.0 px tolerance registered before it was written. The
 * cause is in the trace: **the scroll position is QUANTISED** (integers at
 * DPR 1). An exponential approach proposes ever smaller steps, and once a step
 * falls below one pixel it rounds to no movement at all — so the position
 * stalls a few pixels short, `remaining` never reaches the convergence epsilon,
 * and the run ends on the ceiling below instead of converging. Measured: the
 * destination was quiet from 721 ms and the position crawled 832 -> 823 against
 * a target of 822.06, one pixel per frame, then stopped.
 *
 * So once the step the easing wants is smaller than a pixel, the gap is closed
 * outright. That is at most a one-pixel-step jump — invisible — and it is what
 * makes the convergence test reachable rather than decorative.
 */
const SCROLL_MIN_STEP_PX = 1;

/*
 * 🔴 THERE IS DELIBERATELY NO CLAMP ON THE FRAME DELTA, AND THE FIRST BUILD HAD
 * ONE (100 ms). It made the easing FRAME-RATE DEPENDENT, which is the one
 * property an exponential approach exists to avoid: after a 3,878 ms frame gap
 * the step taken was the one a 100 ms gap deserves, so the position fell behind
 * the wall clock by up to 39x and the ceiling below expired mid-flight.
 *
 * MEASURED on the desktop nav jump at 1280x900, under CDP CPU throttling, on
 * the built artifact — the residual is against `--header-height`:
 *
 *   x1   318 frames, median gap 17 ms, max 485 ms    -> residual   0.1 px
 *   x6    95 frames, median gap 18 ms, max 1,597 ms  -> residual   1.1 px
 *   x20   49 frames, median gap 72 ms, max 3,878 ms  -> residual  96.8 px
 *
 * The gate found it before production did: `scroll-reresolve.spec.js`'s DESKTOP
 * row went red at a 120.15 px residual on a saturated stage, which is why the
 * brief's "assert the desktop path too, or the fix is half-proven" earned its
 * place. With the clamp gone, a long gap yields a step of ~1 and the position
 * lands on the destination in that one frame — on a machine delivering a frame
 * every four seconds the scroll is a jump, which is what every other animation
 * on that machine is too.
 */

/** Same events react-scroll cancelled on, so a visitor taking over still wins. */
const CANCEL_EVENTS = ['mousedown', 'wheel', 'touchmove', 'keydown'];

let activeRun = null;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Where the scroll wants to be RIGHT NOW: the element's current absolute
 * position, less the header, clamped to what the document can actually scroll.
 * Recomputed every frame — that is the whole fix. Returns null while the target
 * does not exist yet, which is normal for a jump into a lazily mounted section.
 */
function currentDestination(name) {
  const el = scroller.get(name);
  if (!el) return null;
  const absoluteTop = el.getBoundingClientRect().top + window.scrollY;
  const maxScroll = Math.max(
    0,
    document.documentElement.scrollHeight - window.innerHeight,
  );
  return Math.max(0, Math.min(absoluteTop - headerHeight(), maxScroll));
}

/**
 * Scroll to a react-scroll `Element` by name, re-resolving the destination on
 * every frame. Replaces `scroller.scrollTo(name, { duration, smooth, offset })`
 * at all 13 nav call sites; the header allowance is read from the one
 * declaration (DWEB-180) here rather than passed in 13 times.
 *
 * @param {string} name  the `Element` name, e.g. 'about'.
 */
export function scrollToSection(name) {
  if (typeof window === 'undefined') return;
  cancelScrollToSection();

  // Under `reduce` the approach is instantaneous (time constant 0) and the loop
  // still runs, because the destination keeps moving after the jump too. That
  // matches index.css's reduced-motion policy: damp the motion, keep the
  // behaviour.
  const timeConstant = prefersReducedMotion() ? 0 : SCROLL_TIME_CONSTANT_MS;

  const startedAt = performance.now();
  const run = { frame: 0, cancelled: false, rafId: 0, stillFrames: 0, lastDestination: null };
  activeRun = run;

  const stop = () => {
    if (run.rafId) cancelAnimationFrame(run.rafId);
    CANCEL_EVENTS.forEach((e) => document.removeEventListener(e, onUserInput));
    if (activeRun === run) activeRun = null;
  };
  function onUserInput() {
    run.cancelled = true;
    stop();
  }
  CANCEL_EVENTS.forEach((e) => document.addEventListener(e, onUserInput, { passive: true }));
  run.stop = stop;

  let lastFrameAt = startedAt;

  const tick = () => {
    if (run.cancelled) return;
    const now = performance.now();
    const elapsed = now - startedAt;
    const delta = now - lastFrameAt;
    lastFrameAt = now;
    run.frame += 1;

    const destination = currentDestination(name);

    // Part 1 of the termination guarantee, checked before anything can continue
    // the loop. 🔴 IT LANDS RATHER THAN ABANDONING: stopping wherever the last
    // partial step left us is how the first build finished 120 px out. The
    // destination is the best answer available at the moment we give up, so the
    // failure mode is "arrived without animating" instead of "stopped short".
    if (elapsed >= SCROLL_MAX_DURATION_MS) {
      if (destination !== null) window.scrollTo(0, destination);
      stop();
      return;
    }
    if (destination === null) {
      // Not mounted yet. Wait for it rather than aborting — the ceiling above
      // is what stops this being unbounded.
      run.rafId = requestAnimationFrame(tick);
      return;
    }

    const destinationMoved =
      run.lastDestination === null ? Infinity : Math.abs(destination - run.lastDestination);
    run.lastDestination = destination;
    const remaining = Math.abs(destination - window.scrollY);

    // Part 2: the destination has settled AND we are on it.
    run.stillFrames =
      destinationMoved <= SCROLL_CONVERGENCE_EPSILON_PX ? run.stillFrames + 1 : 0;
    if (
      run.stillFrames >= SCROLL_CONVERGENCE_FRAMES &&
      remaining <= SCROLL_CONVERGENCE_EPSILON_PX
    ) {
      window.scrollTo(0, destination);
      stop();
      return;
    }

    const gap = destination - window.scrollY;
    const step = timeConstant === 0 ? 1 : 1 - Math.exp(-delta / timeConstant);
    const proposed = gap * step;
    window.scrollTo(
      0,
      Math.abs(proposed) < SCROLL_MIN_STEP_PX ? destination : window.scrollY + proposed,
    );
    run.rafId = requestAnimationFrame(tick);
  };

  run.rafId = requestAnimationFrame(tick);
}

/**
 * Stop any run in flight, releasing its frame and its cancel listeners.
 * Exported so a caller can abandon one deliberately; also how a second nav
 * click supersedes the first.
 */
export function cancelScrollToSection() {
  if (!activeRun) return;
  activeRun.cancelled = true;
  activeRun.stop();
}
